import { Location } from '@angular/common';
import { computed, DestroyRef, inject, Signal, signal, Service } from '@angular/core';
import { Router } from '@angular/router';

import { SubscriptionStore } from './subscription.store';
import { SubscriptionErrorKeys } from '../domain/subscription.errors';
import {
  annualSavingsPct,
  BenefitView,
  canStartTrial,
  CardUpdateCheckout,
  CompareCell,
  CompareRowView,
  freeTierPolicy,
  LimitValue,
  PolicyPerkKind,
  policyLimit,
  premiumTierPolicy,
  SubscriptionPolicy,
  cardLabel,
  CheckoutOutcome,
  DAY_MS,
  formatAmount,
  formatBillDate,
  isUnlimited,
  messagesRemaining,
  PaymentMethodKind,
  planForCycle,
  premiumCountdownKind,
  premiumDaysLeft,
  SUBSCRIPTION_PRICE_SAR,
  trialLengthDays,
  SubscriptionCycle,
  SubscriptionScreen,
  TransitionKind,
  usagePercent,
} from '../domain/models/subscription.model';
import {
  SUBSCRIPTION_BENEFITS,
  SUBSCRIPTION_CANCEL_CHANGES,
  SUBSCRIPTION_COMPARE,
  SUBSCRIPTION_UNLOCKED,
} from '../domain/subscription.catalog';
import { LoggerService, ScopedLogger } from '@core/logging/logger.service';
import { TranslationFacade } from '@core/i18n/application/translation.facade';
import { PlatformService } from '@core/platform/platform.service';
import { LifecycleService } from '@core/lifecycle/lifecycle.service';
import { APP_PATHS } from '@core/routing';
import { ActionState } from '@shared/state/async-action';

/** How the user is settling the charge on the checkout summary. */
export type CheckoutMethod = 'card' | 'wallet';

/** Last-resort copy — only reachable for an action with no fallback key of its own. */
const GENERIC_ERROR_KEY = 'reference_portal_common_error_generic';

/**
 * Backoff for re-checking a checkout the user has come back from.
 *
 * Activation is a WEBHOOK: the gateway redirects the browser back the instant
 * the card clears, which is usually a beat or two BEFORE the webhook has reached
 * our backend. Asking the user to keep tapping "try again" through that window
 * is the wrong shape — so we listen for them, on a widening interval, and stop
 * as soon as the server says premium (or after ~30s, when a manual retry is
 * genuinely the right call).
 */
const CHECKOUT_POLL_DELAYS_MS = [2_000, 4_000, 8_000, 16_000] as const;

/**
 * Screens that sell something. An organization-covered member (`hide_purchase`)
 * never lands on one — they see the covered S1 instead (KHOYEK-SUBSCRIPTION-DAYS-LEFT
 * rule 4 / B2B §9.5).
 */
const PURCHASE_SCREENS: ReadonlySet<SubscriptionScreen> = new Set<SubscriptionScreen>(['S2', 'S3', 'S5', 'S7', 'renew']);

/** The capped count of a limit; null for unlimited / unknown (nothing to quote). */
function countOf(value: LimitValue | null): number | null {
  return value?.kind === 'count' ? value.n : null;
}

/** How the last card update (R3) came back — drives the notice on return. */
export type CardUpdateResult = 'saved' | 'failed';

/**
 * SubscriptionFacade — signals-first public API for the S1–S8 + renew flow.
 *
 * Route-scoped (provided by `provideSubscription`): it owns SCREEN state, the
 * checkout draft (cycle · promo · wallet · card) and navigation. Everything
 * about *what the subscription is* comes from the ROOT {@link SubscriptionStore},
 * so the page, Profile, and every premium gate read one set of signals and a
 * change here is visible everywhere immediately.
 *
 * Components depend ONLY on this. SSR-safe (Router/platform are guarded; the
 * countdown "now" is captured once, on the client, off the render path).
 */
@Service({ autoProvided: false })
export class SubscriptionFacade {
  private readonly store = inject(SubscriptionStore);
  private readonly router = inject(Router);
  private readonly logger: ScopedLogger = inject(LoggerService).forSource('SubscriptionFacade');
  private readonly i18n = inject(TranslationFacade);
  private readonly platform = inject(PlatformService);
  private readonly lifecycle = inject(LifecycleService);
  private readonly location = inject(Location);

  private readonly _screen = signal<SubscriptionScreen>('S1');
  private readonly _transition = signal<TransitionKind | null>(null);
  /** Screens the user came through — powers an in-surface Back that mirrors history. */
  private readonly _backStack = signal<readonly SubscriptionScreen[]>([]);

  // ── checkout draft (local to this visit; never persisted) ──
  private readonly _cycle = signal<SubscriptionCycle>('monthly');
  private readonly _promoCode = signal<string>('');
  private readonly _appliedPromo = signal<string | null>(null);
  private readonly _method = signal<CheckoutMethod>('card');
  private readonly _cardId = signal<string | null>(null);
  /** Set when a checkout came back from the gateway without premium — a real, shown state. */
  private readonly _checkoutUnconfirmed = signal(false);
  /** The card-update charge the server opened, held while the user reads its notice. */
  private readonly _cardUpdate = signal<CardUpdateCheckout | null>(null);
  /** Outcome of a card update the user returned from; null when none / dismissed. */
  private readonly _cardUpdateResult = signal<CardUpdateResult | null>(null);
  /**
   * The gateway's charge id (`tap_id`) from the return url — spent on the FIRST
   * verify only (the fast path); the backoff re-checks just re-read the server.
   */
  private returnChargeId: string | null = null;

  /**
   * One stable "now" captured once on the client (never `Date.now()` per
   * change-detection / render) — keeps the trial/grace countdowns deterministic
   * and hydration-safe. 0 until set (server) ⇒ countdowns clamp via `daysLeft`.
   */
  private readonly _now = signal<number>(0);

  // ─── per-action lifecycles (delegated to the store's) ──────────────────
  /** Initial + retry snapshot read — drives the load skeleton / error-retry. */
  readonly snapshotLoad = this.store.read;
  readonly starting = this.store.trial;
  protected readonly upgrading = this.store.subscribe;
  readonly reactivating = this.store.reactivateAction;
  readonly cancelling = this.store.cancelAction;
  readonly plansLoad = this.store.plansLoad;
  readonly quoteLoad = this.store.quoteLoad;
  readonly invoicesLoad = this.store.invoicesLoad;
  readonly cardsLoad = this.store.cardsLoad;
  readonly autoRenewSaving = this.store.autoRenewAction;
  /** `POST /payment-method` — opening the card-update charge (R3). */
  readonly cardUpdating = this.store.paymentMethodAction;
  /** Renewal reuses `/subscribe`; its own lifecycle keeps the renew button independent. */
  readonly renewing = new ActionState(SubscriptionErrorKeys.renew, 'renew');
  /** The post-checkout verification pass (re-reading the server after the gateway). */
  readonly verifying = new ActionState(SubscriptionErrorKeys.verify, 'verify-checkout');

  // ─── subscription selectors (all from the single root store) ───────────
  readonly snapshot = this.store.snapshot;
  readonly tier = this.store.tier;
  readonly isPremium = this.store.isPremium;
  readonly isPaid = computed(() => this.store.tier() === 'premium');
  readonly isTrial = computed(() => this.store.tier() === 'trial');
  readonly isFree = computed(() => this.store.tier() === 'free');

  // ─── premium countdown (KHOYEK-SUBSCRIPTION-DAYS-LEFT) ─────────────────
  /**
   * Days left on the user's OWN premium access — the server's `days_left`
   * verbatim (never counted down on the device); `null` ⇒ hide every badge.
   * `0` is the LAST day and reads «ينتهي اليوم», never «باقي 0 يوم».
   */
  readonly daysLeft = computed(() => premiumDaysLeft(this.snapshot(), this._now()));
  /**
   * The date the countdown runs to — the server's `premium_ends_at` when it sent
   * one, so the date and the day count can never disagree (grace ends at
   * `grace_ends_at`, not the old period end). Older backend → the same pick locally.
   */
  readonly premiumEndsLabel = computed(() => {
    const s = this.snapshot();
    const legacy = s.state === 'cancelled_grace' ? s.graceEndsAt || s.nextBillAt : s.nextBillAt;
    return formatBillDate(s.premiumEndsAt || legacy, this.i18n.currentLanguage());
  });
  /** Which sentence the badge speaks — `renews` (auto-renew on) is a renewal, not an end. */
  readonly countdownKind = computed(() => premiumCountdownKind(this.snapshot()));
  /** The access ends (or renews) later today. */
  readonly endsToday = computed(() => this.daysLeft() === 0);
  /** Trial countdown; null outside a trial or when the server says there is none. */
  readonly trialDaysLeft = computed(() => (this.countdownKind() === 'trial' ? this.daysLeft() : null));
  /** Filled share (0..100) of the trial bar — only meaningful with a countdown. */
  readonly trialProgressPct = computed(() => {
    const left = this.trialDaysLeft();
    if (left == null) return 0;
    const total = this.trialDays();
    const used = total - Math.min(left, total);
    return (used / total) * 100;
  });

  /** The raw server state (null when offline / cache-only). */
  readonly state = this.store.state;
  /** Cancelled but still inside the 30-day grace (keeps premium) — doc §8.5. */
  readonly inGrace = computed(() => this.state() === 'cancelled_grace');
  /** Grace expired → reverted to free limits; reactivation needs a plan (doc §8.5). */
  readonly isPostCancellation = computed(() => this.state() === 'post_cancellation');
  /** Days left in the cancellation grace window; null when the server reports no countdown. */
  readonly graceDaysLeft = computed(() => (this.countdownKind() === 'grace' ? this.daysLeft() : null));
  /** Paid countdown (renews / ends by auto-renew); null outside `premium_active`. */
  readonly paidDaysLeft = computed(() => {
    const kind = this.countdownKind();
    return kind === 'renews' || kind === 'ends' ? this.daysLeft() : null;
  });
  /** Whether a trial was already consumed — gates the one-time trial CTA (doc §8.3). */
  readonly hadTrial = computed(() => this.snapshot().hadTrial ?? false);
  /** The trial CTA is offered only from a free state with no trial consumed. */
  readonly canTrial = computed(() => canStartTrial(this.snapshot()));
  /** Daily/weekly caps + feature flags from the active plan. */
  readonly policy = this.store.policy;
  /** Today's metered usage (messages/voice) — drives the client-side limit meter. */
  readonly usageToday = this.store.usageToday;
  /** The plan locked on the subscription (real price/cycle/name); null when none. */
  readonly plan = computed(() => this.snapshot().plan ?? null);
  /** True once the first snapshot has resolved — the load skeleton clears on this. */
  readonly loaded = this.store.loaded;
  /** Automatic renewal switch (null when the server doesn't report one). */
  readonly autoRenew = computed(() => this.snapshot().autoRenew);
  readonly paymentMethodOnFile = computed(() => this.snapshot().paymentMethod);
  /** `VISA •••• 4242` for the card the subscription reports on file; '' when none. */
  readonly paymentCardLabel = computed(() => cardLabel(this.snapshot().paymentCard));

  // ─── renewal mandate / card update (SUBSCRIPTION-RECURRING R2–R3) ───────
  /**
   * Only an active PAID subscription can update its card — the server answers
   * `403 not_premium_active` for anyone else, so the CTA is not offered there.
   */
  readonly canUpdateCard = computed(() => this.state() === 'premium_active');
  /**
   * Renewal cannot run until the card is updated: no full mandate on file, or
   * `/auto-renew` refused with 404 («لا توجد بطاقة محفوظة…») — both route to the
   * card update.
   */
  readonly cardUpdateSuggested = computed(
    () =>
      this.canUpdateCard() &&
      (this.snapshot().mandateOnFile === false ||
        (this.autoRenewSaving.failed() && this.autoRenewSaving.status() === 404)),
  );
  /**
   * The server KNOWS there is no mandate, so turning auto-renew on can only 404:
   * the switch shows why it is off and a tap goes straight to the card update
   * instead of sending a write that would fail. (`null` = older backend that
   * doesn't report it → the switch posts as before and a 404 still routes here.)
   */
  readonly autoRenewNeedsCard = computed(
    () => this.canUpdateCard() && this.snapshot().mandateOnFile === false && this.autoRenew() !== true,
  );
  /**
   * The card shown on file cannot renew — `payment_method` can outlive the
   * mandate, so the row is tagged rather than implying renewal will work.
   */
  readonly cardNotRenewable = computed(
    () => this.canUpdateCard() && this.snapshot().mandateOnFile === false && this.paymentMethodOnFile() !== 'wallet',
  );
  /**
   * The /auto-renew error line, minus the 404 «no saved card» case — the
   * mandate-missing notice right below already says that and offers the fix.
   */
  readonly showAutoRenewError = computed(
    () => this.autoRenewSaving.failed() && !(this.cardUpdateSuggested() && this.autoRenewSaving.status() === 404),
  );
  /** The card-update charge awaiting the user's go-ahead (its notice is on screen). */
  readonly cardUpdate = this._cardUpdate.asReadonly();
  /** How the last card update came back. */
  readonly cardUpdateResult = this._cardUpdateResult.asReadonly();

  // ─── organization coverage (B2B §9.5 · KHOYEK-SUBSCRIPTION-DAYS-LEFT rule 4) ──
  /** The organization grant covering this member, or null for everyone else. */
  readonly orgGrant = this.store.orgGrant;
  /** THE flag: an organization covers this member → no purchase UI anywhere here. */
  readonly hidePurchase = this.store.hidePurchase;
  /**
   * The org's own countdown — the top-level `days_left` covers the user's OWN
   * subscription only. Null is an OPEN-ENDED grant, not a missing value.
   */
  readonly orgDaysLeft = computed(() => {
    const days = this.orgGrant()?.daysLeft;
    return days == null ? null : Math.max(0, days);
  });

  // ─── usage meter (doc §6) ─────────────────────────────────────────────
  /** Daily message cap, or null when uncapped/unknown — never render a fake cap. */
  readonly messageLimit = computed(() => {
    const limit = this.policy()?.messageLimit;
    return limit == null || isUnlimited(limit) ? null : limit;
  });
  readonly messagesSent = computed(() => this.usageToday()?.messagesSent ?? null);
  protected readonly messagesLeft = computed(() => messagesRemaining(this.policy(), this.usageToday()));
  readonly usagePct = computed(() => usagePercent(this.policy(), this.usageToday()));
  /** The meter renders only with a real cap AND a real reading behind it. */
  readonly showUsage = computed(() => this.messageLimit() !== null && this.messagesSent() !== null);
  protected readonly voiceLimitMin = computed(() => {
    const limit = this.policy()?.voiceLimitWeeklyMin;
    return limit == null || isUnlimited(limit) ? null : limit;
  });
  readonly memoryEnabled = computed(() => this.policy()?.memoryEnabled ?? null);

  // ─── Free vs Premium copy (MVP 1b · handoff 07) ─────────────────────────
  /**
   * The FREE limits: `free_policy` from `GET /plans`, else the user's own policy
   * while on free, field by field over the handoff 07 values (GAP 01).
   */
  readonly freePolicy = computed(() => freeTierPolicy(this.snapshot(), this.store.catalogFreePolicy()));
  /** The PREMIUM limits — the user's own when premium, else the catalog plan's, over the handoff values. */
  readonly premiumPolicy = computed(() => premiumTierPolicy(this.snapshot(), this.plans(), this._cycle()));
  /** Free weekly voice minutes; null only when the free tier is uncapped. */
  readonly freeVoiceMin = computed(() => countOf(policyLimit(this.freePolicy(), 'voice')));
  /** Free daily messages; null only when the free tier is uncapped. */
  readonly freeMessages = computed(() => countOf(policyLimit(this.freePolicy(), 'messages')));

  /** S1 Free-vs-Premium table, labels resolved (policy cells read the server). */
  readonly compareRows = computed<readonly CompareRowView[]>(() => {
    const free = this.freePolicy();
    const premium = this.premiumPolicy();
    return SUBSCRIPTION_COMPARE.map((row) => ({
      featureKey: row.featureKey,
      feature: this.i18n.translate(row.featureKey),
      free: this.cellText(row.free, free),
      premium: this.cellText(row.premium, premium),
    }));
  });

  /** S1 Premium benefits; the voice row quotes the plan + free limits. */
  readonly benefitRows = computed<readonly BenefitView[]>(() =>
    SUBSCRIPTION_BENEFITS.map((b) =>
      b.limit === 'voice'
        ? { icon: b.icon, title: this.premiumVoiceText(), sub: this.withCount(b.subKey, this.freeVoiceMin()) }
        : { icon: b.icon, title: this.i18n.translate(b.titleKey), sub: this.i18n.translate(b.subKey) },
    ),
  );

  /** "What Premium unlocks" lines (trial / premium screens) — no tools. */
  readonly unlockedLines = computed<readonly string[]>(() =>
    SUBSCRIPTION_UNLOCKED.map((u) => (u.limit === 'voice' ? this.premiumVoiceText() : this.i18n.translate(u.key))),
  );

  /** S4 "what becomes limited" — the messages / voice lines name the free limits only when known. */
  readonly cancelChangeLines = computed<readonly string[]>(() =>
    SUBSCRIPTION_CANCEL_CHANGES.map((c) => {
      if (!c.limit) return this.i18n.translate(c.key);
      const n = c.limit === 'voice' ? this.freeVoiceMin() : this.freeMessages();
      return this.withCount(c.key, n) ?? this.i18n.translate(`${c.key}_free`);
    }),
  );

  /** S5 «أكمل مجاناً» sub-line — each server figure appears only when known. */
  readonly continueFreeSub = computed(() =>
    [
      this.withCount('reference_portal_subscription_continue_free_messages', this.freeMessages()),
      this.withCount('reference_portal_subscription_continue_free_voice', this.freeVoiceMin()),
      this.i18n.translate('reference_portal_subscription_continue_free_memory'),
    ]
      .filter((part): part is string => !!part)
      .join(' · '),
  );

  /**
   * Copy quoting the trial length and the MONTHLY plan price (`GET /plans`). While
   * the price is unknown the `_noprice` variant is shown — never a guessed figure.
   */
  readonly legalText = computed(() => this.trialPriceText('reference_portal_subscription_legal'));
  readonly s3ReassureText = computed(() => this.trialPriceText('reference_portal_subscription_s3_reassure'));
  readonly continuePremiumSub = computed(() => this.trialPriceText('reference_portal_subscription_continue_premium_sub'));

  // ─── plan catalog / pricing ───────────────────────────────────────────
  readonly plans = this.store.plans;
  readonly monthlyPlan = this.store.monthlyPlan;
  protected readonly annualPlan = this.store.annualPlan;
  /** The cycle the user is currently choosing (checkout draft). */
  readonly cycle = this._cycle.asReadonly();
  /** The catalog entry for the chosen cycle — the plan a CTA would buy. */
  readonly selectedPlan = computed(() => planForCycle(this.plans(), this._cycle()));
  /** Whole-percent saving of annual vs 12× monthly; 0 hides the badge. */
  protected readonly annualSavings = computed(() => {
    const monthly = this.monthlyPlan();
    const annual = this.annualPlan();
    return monthly && annual ? annualSavingsPct(monthly.price, annual.price) : 0;
  });

  /**
   * Whether a REAL price is available yet (from the catalog or the user's own
   * subscription) — GAP-009: gates the S1 free-pitch teaser numbers so they show
   * a loading placeholder instead of {@link SUBSCRIPTION_PRICE_SAR} while
   * `/plans` is still in flight, rather than a guessed price the user could
   * mistake for the real one.
   */
  readonly pricingKnown = computed(() => this.selectedPlan() != null || this.plan() != null);
  /**
   * Headline price. Prefers the plan the user is buying (catalog), then the plan
   * already on the subscription, then the design's monthly default as a last-resort
   * numeric safety net for downstream math (see {@link amountDue}) — never rendered
   * directly without checking {@link pricingKnown} first.
   */
  readonly displayPrice = computed(
    () => this.selectedPlan()?.price ?? this.plan()?.price ?? SUBSCRIPTION_PRICE_SAR,
  );
  /** The running subscription's own price (premium/renew screens). */
  readonly planPrice = computed(() => this.plan()?.price ?? SUBSCRIPTION_PRICE_SAR);
  /** i18n key for the billing period of the CHOSEN cycle. */
  readonly periodKey = computed(() => periodKeyFor(this._cycle()));
  /** i18n key for the billing period of the RUNNING subscription. */
  readonly currentPeriodKey = computed(() => periodKeyFor(this.plan()?.cycle ?? this.snapshot().cycle ?? 'monthly'));
  /** Bill date AFTER a renewal — one full cycle past the later of now / next bill. */
  readonly renewPreviewLabel = computed(() => {
    const cycleDays = (this.plan()?.cycle ?? this.snapshot().cycle) === 'annual' ? 365 : 30;
    return formatBillDate(
      Math.max(this._now(), this.snapshot().nextBillAt || 0) + DAY_MS * cycleDays,
      this.i18n.currentLanguage(),
    );
  });

  // ─── checkout summary ─────────────────────────────────────────────────
  readonly quote = this.store.quote;
  readonly cards = this.store.cards;
  readonly invoices = this.store.invoices;
  readonly promoCode = this._promoCode.asReadonly();
  readonly appliedPromo = this._appliedPromo.asReadonly();
  readonly method = this._method.asReadonly();
  readonly selectedCardId = this._cardId.asReadonly();
  protected readonly selectedCard = computed(() => this.cards().find((c) => c.id === this._cardId()) ?? null);
  readonly selectedCardLabel = computed(() => cardLabel(this.selectedCard()));
  /** Wallet credit the quote applied (0 when none / no quote). */
  readonly walletApplied = computed(() => this.quote()?.walletApplied ?? 0);
  /** Promo discount the quote applied (0 when none / no quote). */
  readonly promoDiscount = computed(() => this.quote()?.discount ?? 0);
  /** Net payable — the quote's total, else the plain plan price. */
  protected readonly amountDue = computed(() => this.quote()?.total ?? this.displayPrice());
  /** Display-ready amount (Latin digits, no trailing `.00`). */
  readonly amountDueLabel = computed(() => formatAmount(this.amountDue()));
  readonly subtotalLabel = computed(() => formatAmount(this.quote()?.subtotal ?? this.displayPrice()));
  readonly walletAppliedLabel = computed(() => formatAmount(this.walletApplied()));
  readonly promoDiscountLabel = computed(() => formatAmount(this.promoDiscount()));
  /** The server rejected the entered code — shown inline under the promo field. */
  readonly promoRejected = computed(() => this.quote()?.promoValid === false);
  /** The server's own note about the code/breakdown (already localized). */
  readonly promoMessage = computed(() => this.quote()?.promoMessage ?? null);
  /** Nothing left to pay (wallet + promo covered it) → the CTA says "activate". */
  readonly fullyCovered = computed(() => !!this.quote() && this.amountDue() <= 0);

  // ─── checkout / verification state ────────────────────────────────────
  /** A checkout was handed to the gateway and hasn't been confirmed yet. */
  readonly checkoutPending = computed(() => this.store.pendingCheckout() !== null);
  /** Came back from the gateway but the server still doesn't show premium. */
  readonly checkoutUnconfirmed = this._checkoutUnconfirmed.asReadonly();

  readonly screen = this._screen.asReadonly();
  /**
   * The screen actually rendered: a purchase screen reached by deep link or a
   * stale CTA falls back to S1 for an organization-covered member.
   */
  readonly visibleScreen = computed<SubscriptionScreen>(() => {
    const screen = this._screen();
    return this.hidePurchase() && PURCHASE_SCREENS.has(screen) ? 'S1' : screen;
  });
  /** The hand-off the user is returning from, while one is on record. */
  readonly pendingPurpose = computed(() => this.store.pendingCheckout()?.purpose ?? 'subscribe');
  readonly transition = this._transition.asReadonly();
  /** `resumePremium` dispatches to subscribe OR reactivate — true while either runs. */
  readonly resuming = computed(() => this.upgrading.loading() || this.reactivating.loading());
  /** Either resume path failed — surfaces one inline error under the resume CTA. */
  readonly resumeFailed = computed(() => this.upgrading.failed() || this.reactivating.failed());
  // ─── failure copy ─────────────────────────────────────────────────────
  // Every one of these resolves the SAME way: the backend's own
  // `response.message` when it sent one, otherwise the fallback written for THAT
  // request. Templates bind to these and never to `.error()`, so a user never
  // sees the HTTP layer's one-size-fits-all "an error occurred, try again" when
  // we can tell them which action failed.
  /** `POST /trial` — inline under the S3 confirm button. */
  readonly trialError = this.failureText(this.starting);
  /** `POST /cancel` — inline in the S4 survey and the S5 "stay free" card. */
  readonly cancelError = this.failureText(this.cancelling);
  /** `POST /auto-renew` — inline under the S8 switch. */
  readonly autoRenewError = this.failureText(this.autoRenewSaving);
  /** `POST /payment-method` — inline under the S8 card-update CTA. */
  readonly cardUpdateError = this.failureText(this.cardUpdating);
  /** `GET /invoices` — the S8 billing-history error block. */
  readonly invoicesError = this.failureText(this.invoicesLoad);
  /** `GET /plans` — the S2 picker error block. */
  readonly plansError = this.failureText(this.plansLoad);
  /** `GET /subscription` — the first-read error block. */
  readonly snapshotError = this.failureText(this.snapshotLoad);
  /**
   * Whichever paid path failed. `resumePremium` dispatches to `/subscribe` or
   * `/reactivate`, so the message comes from whichever one actually ran — each
   * with its own fallback ("couldn't start your subscription" vs "couldn't
   * reactivate it").
   */
  readonly resumeError = computed(
    () =>
      (this.reactivating.failed() ? this.resolve(this.reactivating) : null) ??
      (this.upgrading.failed() ? this.resolve(this.upgrading) : null),
  );
  /**
   * Renew runs `/subscribe` under its own lifecycle, so the server's message
   * lands on the STORE's action while `renewing` only records "it threw" —
   * prefer the real one, then renew's own fallback.
   */
  readonly renewError = computed(
    () => this.upgrading.apiMessage() ?? this.i18n.translate(SubscriptionErrorKeys.renew),
  );
  /** The quote call itself failed — the summary is showing the plain plan price. */
  readonly quoteFailed = computed(() => this.quoteLoad.failed());
  /** Trial length: the server's `trial_days`, else 7 (GAP 02). */
  readonly trialDays = computed(() => trialLengthDays(this.snapshot()));

  constructor() {
    // Capture a single client-side "now" (browser-guarded; off the render path).
    if (this.platform.isBrowser) this._now.set(Date.now());
    // Leaving the route must not leave a checkout poll ticking against a facade
    // nobody is watching any more.
    inject(DestroyRef).onDestroy(() => this.cancelCheckoutPoll());
    void this.load();
    void this.store.loadPlans();
    // Authenticated + cosmetic (the S1 tools count) — never on the server.
    if (this.platform.isBrowser) void this.store.loadToolsCount();
  }

  /** Read the snapshot once on entry (idempotent — the store shares in-flight reads). */
  async load(): Promise<void> {
    await this.store.load();
    this.syncCycleToPlan();
  }

  /** Retry after a failed first read. */
  async reload(): Promise<void> {
    await this.store.refresh();
    this.syncCycleToPlan();
  }

  // ─── navigation ───────────────────────────────────────────────────────
  /** Go to a screen, remembering where we came from for {@link back}. */
  goTo(screen: SubscriptionScreen): void {
    const current = this._screen();
    if (current !== screen) this._backStack.update((stack) => [...stack, current]);
    this._screen.set(screen);
    this.logger.uiEvent('Subscription screen', { screen });
  }

  /** Jump to a screen WITHOUT stacking (deep-link entry, or re-anchoring to S1). */
  resetTo(screen: SubscriptionScreen): void {
    this._backStack.set([]);
    this._screen.set(screen);
  }

  /** True when an in-surface Back would move within the flow rather than leave it. */
  readonly canGoBack = computed(() => this._backStack().length > 0);

  /**
   * In-surface Back: pop the screen stack, or leave for the app shell when the
   * stack is empty — so the header arrow and the browser's Back button agree
   * instead of stranding the user on an inner screen.
   */
  back(): void {
    const stack = this._backStack();
    if (!stack.length) {
      this.close();
      return;
    }
    this._backStack.set(stack.slice(0, -1));
    this._screen.set(stack[stack.length - 1]!);
  }

  /**
   * Close the surface → back where the user came from.
   *
   * Premium is reachable from all over the app (Profile's subscription strip, a
   * Khoyek memory-tier upsell, a care gate…), so the X returns to the ACTUAL
   * previous in-app url rather than dumping every visitor on Home. "Real in-app
   * history" is {@link LifecycleService.hasInAppHistory}, never raw
   * `window.history.length` — that count spans the whole browser tab, so popping
   * on it can navigate clean out of the app. With no such predecessor (an
   * SSR-hydrated deep link, a push-notification open, a fresh tab) → Home.
   */
  close(): void {
    if (this.platform.isBrowser && this.lifecycle.hasInAppHistory()) {
      this.location.back();
      return;
    }
    void this.router.navigate([APP_PATHS.HOME]);
  }

  // ─── checkout draft ───────────────────────────────────────────────────
  /** Choose the billing cadence; the money summary re-quotes for it. */
  selectCycle(cycle: SubscriptionCycle): void {
    if (this._cycle() === cycle) return;
    this._cycle.set(cycle);
    this.store.clearQuote();
    void this.refreshQuote();
  }

  setPromoCode(code: string): void {
    this._promoCode.set(code);
  }

  /** Send the typed code to `/quote` — the server decides whether it is valid. */
  async applyPromo(): Promise<void> {
    const code = this._promoCode().trim();
    this._appliedPromo.set(code || null);
    await this.refreshQuote();
  }

  /** Drop the code and re-price without it. */
  async clearPromo(): Promise<void> {
    this._promoCode.set('');
    this._appliedPromo.set(null);
    await this.refreshQuote();
  }

  /** Card vs wallet. Wallet asks `/quote` to spend the balance against the charge. */
  selectMethod(method: CheckoutMethod): void {
    if (this._method() === method) return;
    this._method.set(method);
    void this.refreshQuote();
  }

  selectCard(cardId: string | null): void {
    this._cardId.set(cardId);
  }

  /** Re-price the current draft (cycle · promo · wallet). Never throws. */
  protected async refreshQuote(): Promise<void> {
    await this.store.requestQuote(this.draft());
  }

  /**
   * Open the checkout summary for a cycle: load the catalog + saved cards and
   * price the draft, then show S7. Everything is best-effort — a failed quote or
   * card read still lands the user on a usable summary.
   */
  async openCheckout(cycle?: SubscriptionCycle): Promise<void> {
    // Switching cadence on the way in DROPS the old breakdown. Keeping it would let
    // S7 paint the PREVIOUS cycle's total — with an enabled "Pay now" under it —
    // for the round-trip it takes the new quote to land. A cleared quote falls back
    // to the plan price, which is at least the right plan's number.
    if (cycle && cycle !== this._cycle()) {
      this._cycle.set(cycle);
      this.store.clearQuote();
    }
    this.goTo('S7');
    // Catalog and saved cards are independent reads — run them CONCURRENTLY, but
    // await BOTH: the default-card pick below reads `cards()`, so firing that read
    // and not waiting for it would pick from an empty list every time.
    await Promise.all([this.store.loadPlans(), this.store.loadCards()]);
    // Pre-select the card on file (only when the user hasn't chosen one).
    if (!this._cardId()) this._cardId.set(this.cards().find((c) => c.isDefault)?.id ?? null);
    // Quote LAST: it must price the plan and card that are now settled.
    await this.refreshQuote();
  }

  /** Open the plan picker (doc §8.4). */
  async openPlans(): Promise<void> {
    this.goTo('S2');
    await this.store.loadPlans();
  }

  /** Open the manage/billing screen (auto-renew · method · invoices · cancel). */
  async openBilling(): Promise<void> {
    this.goTo('S8');
    // Both feed the screen; neither depends on the other, so run them together
    // and await both so the screen settles in one pass instead of two repaints.
    await Promise.all([this.store.loadCards(), this.store.loadInvoices()]);
  }

  /** Retry the billing history read. */
  reloadInvoices(): Promise<void> {
    return this.store.loadInvoices(true);
  }

  // ─── tier actions ─────────────────────────────────────────────────────
  /** S3 — free → trial; celebrates with the orange trial transition. */
  async startTrial(): Promise<void> {
    try {
      await this.store.startTrial();
      this._transition.set('trial');
    } catch {
      // Already recorded on `starting` (and toasted by AppHttp). The screen binds
      // `trialError()`, which is the server's own message or the trial-specific
      // fallback — never a generic line.
    }
  }

  /**
   * Buy premium. Routes to `/subscribe` or `/reactivate` by server state, then
   * acts on what the server asked for: hand the tab to the hosted checkout, or —
   * when nothing was left to pay — celebrate the activation it already made.
   */
  async checkout(): Promise<void> {
    // Without a resolvable plan there is nothing to buy — retry the catalog once,
    // then hand the user to the picker (which owns the empty/error + retry state)
    // rather than letting the CTA silently do nothing.
    if (!(await this.ensurePlan())) return;
    try {
      const outcome = await this.store.resumePremium(this.draft());
      this.handleOutcome(outcome, 'upgrade');
    } catch {
      /* surfaced through resumeFailed/resumeError */
    }
  }

  /** Legacy entry point kept for the direct CTAs — same routing as {@link checkout}. */
  protected resumePremium(cycle?: SubscriptionCycle): Promise<void> {
    if (cycle) this._cycle.set(cycle);
    return this.checkout();
  }

  /** trial / free → paid premium (`/subscribe`). */
  async upgrade(cycle?: SubscriptionCycle): Promise<void> {
    if (cycle) this._cycle.set(cycle);
    if (!(await this.ensurePlan())) return;
    try {
      this.handleOutcome(await this.store.subscribeTo(this.draft()), 'upgrade');
    } catch {
      /* surfaced through upgrading */
    }
  }

  /** cancelled_grace / post_cancellation → paid premium (`/reactivate`, doc §8.5). */
  async reactivate(cycle?: SubscriptionCycle): Promise<void> {
    if (cycle) this._cycle.set(cycle);
    if (!(await this.ensurePlan())) return;
    try {
      this.handleOutcome(await this.store.reactivate(this.draft()), 'upgrade');
    } catch {
      /* surfaced through reactivating */
    }
  }

  /**
   * renew screen — an active subscriber buying the next cycle. The v3 API has no
   * dedicated renew endpoint; `/subscribe` is the correct call (reactivate is
   * reserved for grace / post-cancellation).
   */
  async renew(): Promise<void> {
    if (!(await this.ensurePlan())) return;
    const outcome = await this.renewing.run(() => this.store.subscribeTo(this.draft()));
    if (outcome) this.handleOutcome(outcome, 'renew');
  }

  /** S4/S5 — cancel, carrying the optional exit-survey reason. */
  async cancel(reason?: string | null): Promise<void> {
    try {
      await this.store.cancel(reason);
    } catch {
      /* surfaced through cancelling */
    }
  }

  /**
   * Manage screen — automatic renewal. Product decision: renewal is always ON,
   * so the value sent is pinned to `true` whatever the caller asks for.
   */
  async setAutoRenew(_on?: boolean): Promise<void> {
    try {
      await this.store.setAutoRenew(true);
    } catch {
      /* surfaced through autoRenewSaving */
    }
  }

  /**
   * The S8 switch. Turning renewal ON with no mandate on file goes to the card
   * update (which turns renewal back on by itself once the card saves, R2) —
   * never to a `/auto-renew` call the server would refuse with 404.
   */
  async toggleAutoRenew(): Promise<void> {
    const turnOn = this.autoRenew() !== true;
    if (turnOn && this.autoRenewNeedsCard()) {
      await this.requestCardUpdate();
      return;
    }
    await this.setAutoRenew(turnOn);
  }

  /**
   * Manage screen — «تغيير البطاقة» / «تحديث طريقة الدفع» (R3). Opens the
   * server's verification charge and holds it so its notice (the charge + full
   * refund) is read BEFORE the gateway opens; nothing is paid until the user
   * continues with {@link confirmCardUpdate}.
   */
  async requestCardUpdate(): Promise<void> {
    this._cardUpdateResult.set(null);
    try {
      const checkout = await this.store.requestCardUpdate();
      if (checkout.url) this._cardUpdate.set(checkout);
      // No page to open (offline harness) — nothing was charged, nothing to confirm.
    } catch {
      /* surfaced through cardUpdateError (403 not_premium_active · 400 retry) */
    }
  }

  /** Continue to the hosted page; the return trip confirms the MANDATE, not premium. */
  confirmCardUpdate(): void {
    const checkout = this._cardUpdate();
    if (!checkout?.url) return;
    this.store.handOffCardUpdate();
    this._cardUpdate.set(null);
    this.platform.redirectExternal(checkout.url);
  }

  /** Back out of the card-update notice — the hosted page is never opened. */
  cancelCardUpdate(): void {
    this._cardUpdate.set(null);
  }

  /** Dismiss the card-update result notice. */
  dismissCardUpdateResult(): void {
    this._cardUpdateResult.set(null);
  }

  /**
   * The app came back to the foreground: re-read the subscription so the
   * countdown is the server's current one (KHOYEK-SUBSCRIPTION-DAYS-LEFT rule 5 —
   * never counted down on the device). Shares any read already in flight.
   */
  async refreshOnForeground(): Promise<void> {
    if (this.platform.isBrowser) this._now.set(Date.now());
    await this.store.refresh();
  }

  // ─── checkout return ──────────────────────────────────────────────────
  /**
   * Called on entry (and whenever the tab regains focus) after a checkout was
   * handed off: re-read the server and tell the user the truth — activated, or
   * still not confirmed. Never invents premium.
   */
  async verifyCheckout(chargeId: string | null = null): Promise<void> {
    if (chargeId) this.returnChargeId = chargeId;
    if (!this.store.pendingCheckout()) {
      this.cancelCheckoutPoll();
      return;
    }
    const charge = this.returnChargeId;
    this.returnChargeId = null;
    const resolution = await this.verifying.run(() => this.store.resolveCheckoutReturn(charge));
    if (resolution && resolution.status !== 'pending') {
      this.cancelCheckoutPoll();
      this._checkoutUnconfirmed.set(false);
      if (resolution.purpose === 'card_update') {
        // The user was premium all along — confirm the card, never celebrate an upgrade.
        this._cardUpdateResult.set(resolution.status === 'confirmed' ? 'saved' : 'failed');
      } else {
        this._transition.set('upgrade');
      }
      return;
    }
    // Still pending: the webhook is simply slower than the redirect. Say so, and
    // keep listening rather than making the user poll us by hand.
    const stillPending = this.store.pendingCheckout() !== null;
    this._checkoutUnconfirmed.set(stillPending);
    if (stillPending) this.scheduleCheckoutPoll();
    else this.cancelCheckoutPoll();
  }

  /**
   * Re-verify from scratch — the user returned to the tab, so give the backoff a
   * clean slate (a webhook that was late 30s ago may well have landed since).
   */
  async recheckCheckout(): Promise<void> {
    this.checkoutPollAttempt = 0;
    await this.verifyCheckout();
  }

  /** Dismiss the "not confirmed yet" notice and stop watching that checkout. */
  dismissCheckoutNotice(): void {
    this.cancelCheckoutPoll();
    this._checkoutUnconfirmed.set(false);
    this.store.clearCheckoutPending();
  }

  /** Dismiss the transition overlay and re-anchor S1 to the new tier. */
  completeTransition(): void {
    this._transition.set(null);
    this.resetTo('S1');
  }

  // ─── internals ────────────────────────────────────────────────────────
  /**
   * THE resolution rule for every failure message on this surface:
   * the backend's `response.message` when it sent one, else the fallback copy
   * written for the specific request that failed. Never returns a bare null and
   * never returns the HTTP layer's generic default.
   */
  private resolve(action: ActionState): string {
    return action.apiMessage() ?? this.i18n.translate(action.fallbackKey ?? GENERIC_ERROR_KEY);
  }

  // ── checkout listener (browser-only; torn down with the route) ──
  private checkoutPoll: ReturnType<typeof setTimeout> | null = null;
  private checkoutPollAttempt = 0;

  /**
   * Queue the next re-check on the {@link CHECKOUT_POLL_DELAYS_MS} backoff.
   * Browser-only, single-timer (never stacks), and bounded — once the delays run
   * out we stop and leave the manual "try again" as the honest next step.
   */
  private scheduleCheckoutPoll(): void {
    if (!this.platform.isBrowser) return;
    const delay = CHECKOUT_POLL_DELAYS_MS[this.checkoutPollAttempt];
    if (delay === undefined) {
      this.logger.uiEvent('Checkout still unconfirmed after backoff — manual retry only');
      return;
    }
    this.checkoutPollAttempt += 1;
    this.cancelCheckoutPoll();
    this.checkoutPoll = setTimeout(() => {
      this.checkoutPoll = null;
      void this.verifyCheckout();
    }, delay);
  }

  private cancelCheckoutPoll(): void {
    if (this.checkoutPoll === null) return;
    clearTimeout(this.checkoutPoll);
    this.checkoutPoll = null;
  }

  /**
   * A signal of {@link resolve}'s answer, empty while the action has not failed —
   * so a template can bind one expression and let `@if` do the rest. Re-computes
   * on a language switch because `translate` reads the language signal.
   */
  private failureText(action: ActionState): Signal<string> {
    return computed(() => (action.failed() ? this.resolve(action) : ''));
  }

  /** One compare cell: the tier policy's value (server first, handoff fallback), or a spec line. */
  private cellText(cell: CompareCell, policy: SubscriptionPolicy): string {
    if (cell.kind === 'spec') return this.i18n.translate(cell.key);
    if (cell.kind === 'perk') return this.perkText(cell.perk, policy);
    if (cell.kind === 'tools') {
      return (
        this.withCount('reference_portal_subscription_val_tools_n', this.store.toolsCount()) ??
        this.i18n.translate('reference_portal_subscription_val_tools_all')
      );
    }
    const value = policyLimit(policy, cell.limit);
    if (value?.kind === 'unlimited') return this.i18n.translate('reference_portal_subscription_val_unlimited');
    if (value?.kind === 'count') {
      const key = cell.limit === 'messages' ? 'reference_portal_subscription_val_messages_n' : 'reference_portal_subscription_val_voice_n';
      return this.withCount(key, value.n) ?? '';
    }
    return this.i18n.translate(cell.fallbackKey ?? 'reference_portal_subscription_val_unknown');
  }

  /** Memory days · group session price · reply priority as a compare cell. */
  private perkText(perk: PolicyPerkKind, policy: SubscriptionPolicy): string {
    if (perk === 'priority') {
      return this.i18n.translate(
        policy.priorityReply ? 'reference_portal_subscription_val_priority_premium' : 'reference_portal_subscription_val_none',
      );
    }
    if (perk === 'groups') {
      const price = policy.groupSessionPrice;
      if (price == null) return this.i18n.translate('reference_portal_subscription_val_unknown');
      return price > 0
        ? (this.withCount('reference_portal_subscription_val_groups_price_n', price) ?? '')
        : this.i18n.translate('reference_portal_subscription_val_groups_premium');
    }
    const days = policy.memoryDays;
    if (days == null) return this.i18n.translate('reference_portal_subscription_val_unknown');
    if (days < 0) return this.i18n.translate('reference_portal_subscription_val_memory_forever');
    // Arabic counts 3–10 with the plural «أيام», 11+ with the singular «يوم».
    const key = days >= 3 && days <= 10 ? 'reference_portal_subscription_val_memory_days_few' : 'reference_portal_subscription_val_memory_days_n';
    return this.withCount(key, days) ?? '';
  }

  /**
   * The Premium voice line from the plan policy: «بلا حدود» for `-1`, «{n} دقيقة»
   * for a cap (the server's 60, not the handoff's ∞), a number-free line while unknown.
   */
  private premiumVoiceText(): string {
    const value = policyLimit(this.premiumPolicy(), 'voice');
    if (value?.kind === 'unlimited') return this.i18n.translate('reference_portal_subscription_benefit_voice');
    if (value?.kind === 'count') return this.withCount('reference_portal_subscription_benefit_voice_n', value.n) ?? '';
    return this.i18n.translate('reference_portal_subscription_benefit_voice_more');
  }

  /** `key` with `{d}` (trial days) and `{price}` (monthly plan) filled; `${key}_noprice` while the price is unknown. */
  private trialPriceText(key: string): string {
    const price = this.monthlyPlan()?.price;
    const text = price == null ? this.i18n.translate(`${key}_noprice`) : this.i18n.translate(key).replace('{price}', String(price));
    return text.replace('{d}', String(this.trialDays()));
  }

  /** `key`'s copy with `{n}` filled, or null when there is no number to fill. */
  private withCount(key: string, n: number | null): string | null {
    return n == null ? null : this.i18n.translate(key).replace('{n}', String(n));
  }

  /**
   * Guarantee a buyable plan before any paid call. Re-reads the public catalog
   * once; if it still comes back empty the user goes to the picker, which shows
   * the honest empty/error state with a retry instead of a CTA that does nothing.
   */
  private async ensurePlan(): Promise<boolean> {
    if (this.selectedPlan()) return true;
    await this.store.loadPlans(true);
    if (this.selectedPlan()) return true;
    this.goTo('S2');
    return false;
  }

  /** The current checkout draft as store options. */
  private draft() {
    return {
      cycle: this._cycle(),
      planId: this.selectedPlan()?.id,
      promoCode: this._appliedPromo(),
      useWallet: this._method() === 'wallet',
      cardId: this._method() === 'card' ? this._cardId() : null,
      paymentMethod: (this._method() === 'wallet' ? 'wallet' : 'card') as PaymentMethodKind,
    };
  }

  /**
   * Act on what `/subscribe|/reactivate` returned:
   *  · redirect  — hand the CURRENT tab to the hosted checkout, so the gateway's
   *                return lands back here (no orphan tab, no popup blocker)
   *  · activated — the charge settled inline; celebrate
   *  · pending   — accepted but not yet confirmed; say so rather than celebrating
   */
  private handleOutcome(outcome: CheckoutOutcome, celebrate: TransitionKind): void {
    if (outcome.kind === 'redirect') {
      this.platform.redirectExternal(outcome.url);
      return;
    }
    if (outcome.kind === 'activated') {
      this._checkoutUnconfirmed.set(false);
      this._transition.set(celebrate);
      return;
    }
    this._checkoutUnconfirmed.set(true);
  }

  /** Start the checkout draft on the cadence the user already subscribes to. */
  private syncCycleToPlan(): void {
    const cycle = this.plan()?.cycle ?? this.snapshot().cycle;
    if (cycle) this._cycle.set(cycle);
  }
}

/** i18n key for a cadence's billing-period suffix. */
function periodKeyFor(cycle: SubscriptionCycle): string {
  return cycle === 'annual'
    ? 'reference_portal_subscription_per_year'
    : 'reference_portal_subscription_per_month';
}
